import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Grid,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Visibility as VisionIcon,
  Psychology as UserResearchIcon,
  Architecture as RoadmapIcon,
  Speed as AgileIcon,
  Layers as FeaturesIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';
import HolographicStepper, { StepData } from '../common/HolographicStepper.tsx';
import GradientText from '../common/GradientText.tsx';
import EditableStepContent from '../common/EditableStepContent.tsx';

interface ProductStrategyStepperProps {
  productName?: string;
  initialStep?: number;
  onComplete?: () => void;
  onSubmitRFP?: () => void;
}

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.4)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(2),
}));

const MetricChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: 'rgba(0, 204, 153, 0.1)',
  color: '#00CC99',
  border: '1px solid rgba(0, 204, 153, 0.3)',
  '&:hover': {
    backgroundColor: 'rgba(0, 204, 153, 0.2)',
  }
}));

const ProductStrategyStepper: React.FC<ProductStrategyStepperProps> = ({
  productName = "Product",
  initialStep = 0,
  onComplete,
  onSubmitRFP,
}) => {
  // Add state for costs
  const [stepCosts, setStepCosts] = useState({
    0: 2800, // Product Vision & Strategy
    1: 3400, // User Research & Personas
    2: 3800, // Product Roadmap
    3: 2600, // Agile Development Process
    4: 3200, // Feature Prioritization
    5: 4500, // Go-to-Market Strategy
  });

  // Function to update a step's cost
  const handleCostChange = (stepIndex: number, newCost: number) => {
    setStepCosts(prev => ({
      ...prev,
      [stepIndex]: newCost
    }));
  };

  // Define the product strategy steps
  const productSteps: StepData[] = [
    {
      label: 'Product Vision & Strategy',
      description: 'Define the product vision, goals, and strategic direction',
      icon: <VisionIcon />,
      cost: stepCosts[0],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Product Vision & Strategy for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Product Vision Statement"
            cost={stepCosts[0]}
            onCostChange={(newCost) => handleCostChange(0, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Articulate a clear, compelling vision for the product that aligns with business objectives and addresses customer needs. Define what success looks like in 1-3 years.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Strategic Objectives
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <VisionIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Market Positioning" 
                  secondary="Define how the product will be positioned in the market relative to competitors" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <VisionIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Value Proposition" 
                  secondary="Articulate the unique value the product delivers to customers" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
            </List>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'User Research & Personas',
      description: 'Understand user needs, behaviors, and create detailed personas',
      icon: <UserResearchIcon />,
      cost: stepCosts[1],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            User Research & Personas for {productName}
          </GradientText>
          
          <EditableStepContent
            title="User Research Methodology"
            cost={stepCosts[1]}
            onCostChange={(newCost) => handleCostChange(1, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                - Conduct user interviews, surveys, and usability testing to gather insights.
                - Analyze user behavior data and feedback to identify patterns and pain points.
              </Typography>
            }
          />
            
          <Divider sx={{ my: 2, bgcolor: 'rgba(138, 43, 226, 0.2)' }} />
            
          <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
            User Personas
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(138, 43, 226, 0.1)', height: '100%' }}>
                <Typography variant="subtitle2" sx={{ color: '#8A2BE2', mb: 1 }}>
                  Primary Persona: Enterprise Manager
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Mid-level manager at a large enterprise, focused on improving team efficiency and reporting metrics to leadership.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(0, 204, 153, 0.1)', height: '100%' }}>
                <Typography variant="subtitle2" sx={{ color: '#00CC99', mb: 1 }}>
                  Secondary Persona: Technical Implementer
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  IT professional responsible for integrating and maintaining systems, concerned with technical details and compatibility.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )
    },
    {
      label: 'Product Roadmap',
      description: 'Create a strategic roadmap with key milestones and deliverables',
      icon: <RoadmapIcon />,
      cost: stepCosts[2],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Product Roadmap for {productName}
          </GradientText>
          
          <EditableStepContent
            title="12-Month Roadmap"
            cost={stepCosts[2]}
            onCostChange={(newCost) => handleCostChange(2, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                - Outline key milestones, features, and releases planned for the next 12 months.
                - Align roadmap with business objectives and user needs.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Quarterly Objectives
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <RoadmapIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Q1: Foundation" 
                  secondary="Core platform development, basic AI functionality, initial user testing" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <RoadmapIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Q2: Expansion" 
                  secondary="Advanced features, integration capabilities, beta program launch" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <RoadmapIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Q3: Refinement" 
                  secondary="Performance optimization, user experience enhancements, full market launch" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <RoadmapIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Q4: Scaling" 
                  secondary="Enterprise features, advanced analytics, expansion to new market segments" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
            </List>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Agile Development Process',
      description: 'Define the agile methodology and development workflow',
      icon: <AgileIcon />,
      cost: stepCosts[3],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Agile Development Process for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Development Methodology"
            cost={stepCosts[3]}
            onCostChange={(newCost) => handleCostChange(3, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                - Implement Scrum or Kanban methodology with 2-week sprints.
                - Establish regular ceremonies: sprint planning, daily standups, sprint reviews, and retrospectives.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Continuous Improvement
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(138, 43, 226, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#8A2BE2', mb: 1 }}>
                    Feedback Loops
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Implement user feedback mechanisms, beta testing programs, and feature flagging for controlled rollouts.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(255, 111, 0, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ color: '#FF6F00', mb: 1 }}>
                    Metrics & Analytics
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Track key metrics like user engagement, feature adoption, and performance to inform future development.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Feature Prioritization',
      description: 'Prioritize features based on user value and business impact',
      icon: <FeaturesIcon />,
      cost: stepCosts[4],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Feature Prioritization for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Prioritization Framework"
            cost={stepCosts[4]}
            onCostChange={(newCost) => handleCostChange(4, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                - Use RICE scoring (Reach, Impact, Confidence, Effort) to prioritize features.
                - Balance user needs, business goals, and technical feasibility.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Feature Categories
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2 }}>
              <MetricChip label="Must-Have" />
              <MetricChip label="High Value" />
              <MetricChip label="Nice-to-Have" />
              <MetricChip label="Future Consideration" />
            </Box>
            
            <Divider sx={{ my: 2, bgcolor: 'rgba(138, 43, 226, 0.2)' }} />
            
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              MVP Definition
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              - Define the minimum viable product that delivers core value to users.
              - Identify critical features required for market launch.
            </Typography>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Go-to-Market Strategy',
      description: 'Plan the product launch and marketing approach',
      icon: <LaunchIcon />,
      cost: stepCosts[5],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Go-to-Market Strategy for {productName}
          </GradientText>
          
          <EditableStepContent
            title="Launch Plan"
            cost={stepCosts[5]}
            onCostChange={(newCost) => handleCostChange(5, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                - Define launch phases: alpha, beta, limited release, and general availability.
                - Establish success criteria and KPIs for each phase.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Marketing & Sales Enablement
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <LaunchIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Product Messaging" 
                  secondary="Develop clear, compelling messaging that highlights the product's value proposition" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 36, color: '#00CC99' }}>
                  <LaunchIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Sales Collateral" 
                  secondary="Create demos, case studies, and sales materials to support the go-to-market effort" 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
            </List>
          </ContentBox>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <HolographicStepper 
        steps={productSteps}
        initialStep={initialStep}
        onComplete={onComplete}
        orientation="horizontal"
        alternativeLabel={true}
      />
    </Box>
  );
};

export default ProductStrategyStepper; 